import { useParams } from '@tanstack/react-router';
import { Clock, Pencil, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TimeEntry } from '@tt/shared';
import { Button } from '@/components/ui/button';
import { Alert, Badge, Spinner } from '@/components/ui/misc';
import { describeError } from '@/lib/errors';
import { formatDate, formatHours } from '@/lib/format';
import { useEntries } from '@/lib/queries';
import { useFormat } from '@/lib/use-format';
import { useTimesheetLock } from '@/lib/use-timesheet-lock';
import { ClosedPeriodNotice } from './closed-period-notice';
import { EntryFormDialog } from './entry-form-dialog';
import { TimeNav } from './time-nav';
import { Swatch } from '@/components/ui/swatch';

export function DayView() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { date } = useParams({ from: '/app/time/day/$date' });
  const entries = useEntries(date, date);
  const locked = useTimesheetLock().isLocked(date);
  const [dialog, setDialog] = useState<{ open: boolean; entry: TimeEntry | null }>({
    open: false,
    entry: null,
  });
  const defaults = useMemo(() => ({ entryDate: date }), [date]);

  const total = entries.data?.totals.total ?? 0;
  const list = entries.data?.data ?? [];

  return (
    <div>
      <TimeNav
        view="day"
        anchor={date}
        title={formatDate(date, fmt, { dateStyle: 'full' })}
        total={formatHours(total, fmt)}
      >
        {/* No way in on a closed day: an "Add entry" button that only ever produces a refusal is
            worse than no button, and the notice below says why it is gone. */}
        {locked ? null : (
          <Button onClick={() => setDialog({ open: true, entry: null })}>
            <Plus /> {t('time.addEntry')}
          </Button>
        )}
      </TimeNav>

      <ClosedPeriodNotice covers={[date]} />

      {entries.isPending ? <Spinner /> : null}
      {entries.isError ? <Alert variant="error">{describeError(entries.error, t)}</Alert> : null}

      {entries.isSuccess && list.length === 0 ? (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t('time.noEntries')}
        </p>
      ) : null}

      {list.length > 0 ? (
        <ul className="divide-y rounded-lg border bg-background" aria-label={t('time.dayView')}>
          {list.map((entry) => (
            <li key={entry.id} className="flex items-start gap-3 p-3 sm:items-center">
              <Swatch color={entry.project.color} className="mt-1 h-3 w-3 shrink-0 sm:mt-0" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium">{entry.project.name}</span>
                  <span className="text-muted-foreground">/</span>
                  <span>{entry.activity.name}</span>
                  {entry.project.status === 'ARCHIVED' || entry.activity.status === 'ARCHIVED' ? (
                    <Badge>{t('time.archived')}</Badge>
                  ) : null}
                  {entry.activity.type === 'PERSONAL' ? <Badge>{t('time.personal')}</Badge> : null}
                  {entry.source === 'TIMER' ? (
                    <span title={t('time.sourceTimer')} className="text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" aria-label={t('time.sourceTimer')} />
                    </span>
                  ) : null}
                </div>
                {entry.description ? (
                  <p className="mt-0.5 truncate text-sm text-muted-foreground">
                    {entry.description}
                  </p>
                ) : null}
              </div>
              <span className="shrink-0 text-sm font-semibold tabular-nums">
                {formatHours(entry.durationMinutes, fmt)}
              </span>
              {locked ? null : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDialog({ open: true, entry })}
                  aria-label={`${t('app.edit')}: ${entry.project.name} / ${entry.activity.name}`}
                >
                  <Pencil />
                </Button>
              )}
            </li>
          ))}
          <li className="flex items-center justify-between bg-muted/40 p-3 text-sm font-semibold">
            <span>{t('time.dailyTotal')}</span>
            <span className="tabular-nums">{formatHours(total, fmt)}</span>
          </li>
        </ul>
      ) : null}

      <EntryFormDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((s) => ({ ...s, open }))}
        entry={dialog.entry}
        defaults={defaults}
      />
    </div>
  );
}
